import { AxiosResponse } from 'axios';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';
import { mapFxTransactionImport } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import {
  asResponse,
  paginationRange,
  buildPaginatedResponse,
  throwIfError,
} from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { FxTransactionImport } from '@/types/api/finance';

const financeImportsService = {
  async getAll({
    page,
    take,
  }: {
    page: number;
    take: number;
  }): Promise<AxiosResponse<ResponseWithPagination<FxTransactionImport[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_transaction_imports')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    query = applyOrgFilter(query);

    const { data, error, count } = await query;
    if (error) throw error;
    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapFxTransactionImport), count ?? 0, page, take)
    );
  },

  async getById(id: string): Promise<AxiosResponse<FxTransactionImport>> {
    const { data, error } = await supabase
      .from('fx_transaction_imports')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return asResponse(mapFxTransactionImport(data));
  },

  async create(payload: {
    fileName: string;
    rowCount: number;
    sourceAccount?: string;
    importedBy?: string;
  }): Promise<AxiosResponse<FxTransactionImport>> {
    const orgId = getActiveOrgId();
    const insert: Record<string, unknown> = {
      file_name: payload.fileName,
      row_count: payload.rowCount,
      status: 'pending',
      ...(orgId ? { organization_id: orgId } : {}),
    };
    if (payload.sourceAccount !== undefined) insert.source_account = payload.sourceAccount;
    if (payload.importedBy !== undefined) insert.imported_by = payload.importedBy;

    const result = await supabase.from('fx_transaction_imports').insert(insert).select().single();

    return asResponse(mapFxTransactionImport(throwIfError(result)));
  },

  async update(
    id: string,
    payload: {
      status?: string;
      matchedCount?: number;
      aiMatchedCount?: number;
      unmatchedCount?: number;
      errorMessage?: string;
    }
  ): Promise<AxiosResponse<FxTransactionImport>> {
    const updates: Record<string, unknown> = {};
    if (payload.status !== undefined) updates.status = payload.status;
    if (payload.matchedCount !== undefined) updates.matched_count = payload.matchedCount;
    if (payload.aiMatchedCount !== undefined) updates.ai_matched_count = payload.aiMatchedCount;
    if (payload.unmatchedCount !== undefined) updates.unmatched_count = payload.unmatchedCount;
    if (payload.errorMessage !== undefined) updates.error_message = payload.errorMessage;

    const result = await supabase
      .from('fx_transaction_imports')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    return asResponse(mapFxTransactionImport(throwIfError(result)));
  },
};

export default financeImportsService;
